import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, Chip, Grid, Card, CardContent, Skeleton } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { locationsAPI, eventsAPI, type EventType, type Event } from '../services/api';

const EventTypePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [eventType, setEventType] = useState<EventType | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const types = await locationsAPI.getEventTypes();
        const found = types.find(t => t.id === Number(id));
        setEventType(found || null);

        const response = await eventsAPI.getEvents({ type_id: Number(id), limit: 20 });
        setEvents(response.events);
      } catch (err) {
        console.error('Failed to load event type:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={300} height={30} sx={{ mb: 3 }} />
          <Grid container spacing={2}>
            {[1, 2, 3].map(i => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          size="small"
          sx={{ mb: 2 }}
        >
          На главную
        </Button>

        <Typography variant="h4" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.3rem', sm: '2rem' } }}>
          {eventType?.icon_emoji || '🎯'} {eventType?.name || 'Вид мероприятия'}
        </Typography>
        {eventType?.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {eventType.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Мероприятия ({events.length})
          </Typography>
          <Button variant="contained" size="small" onClick={() => navigate(`/events?type_id=${id}`)}>
            Все мероприятия
          </Button>
        </Box>

        {events.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              Пока нет мероприятий этого типа
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {events.map((event) => {
              const participantsCount = event.reserved_seats + event.current_participants;
              return (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <Card
                    onClick={() => navigate(`/events/${event.id}`)}
                    sx={{ cursor: 'pointer', height: '100%' }}
                  >
                    <CardContent>
                      <Chip
                        label={event.status === 'open' ? 'Открыт набор' : 'Завершен'}
                        size="small"
                        color={event.status === 'open' ? 'success' : 'default'}
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom noWrap>
                        {event.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(event.start_datetime)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {event.city}{event.metro_station ? `, м. ${event.metro_station}` : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {participantsCount}/{event.max_participants} участников
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default EventTypePage;